import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Grid, useMediaQuery, useTheme, Button } from '@mui/material';
import Image from 'next/image';
import ControlPointIcon from '@mui/icons-material/ControlPoint';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { StyledScroll } from './ProjectFloorPlanSection';
import { useTranslation } from 'next-i18next';

export interface ProjectIdeaBoxProps {
  imageUrl: string;
  title?: string;
  descriptionHtml: string;
}

export const ProjectIdeaBox = ({ imageUrl, title, descriptionHtml }: ProjectIdeaBoxProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);

  useEffect(() => {
    const checkHeight = () => {
      if (!isDesktop && contentRef.current) {
        const el = contentRef.current;
        requestAnimationFrame(() => {
          setShowReadMore(el.scrollHeight > 200);
        });
      }
    };

    checkHeight();

    window.addEventListener('resize', checkHeight);
    return () => window.removeEventListener('resize', checkHeight);
  }, [descriptionHtml, isDesktop]);

  return (
    <Box sx={{ borderRadius: '20px', overflow: 'hidden' }}>
      <Grid
        container
        spacing={0}
        direction={isDesktop ? 'row' : 'column'}
        alignItems={isDesktop ? 'stretch' : 'center'}
      >
        {/* image */}
        {imageUrl && (
          <Grid item xs={12} md={6} sx={{ order: 1, width: '100%' }}>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: { xs: 220, md: 400 },
                overflow: 'hidden',
                bgcolor: '#ddd',
              }}
            >
              <Image
                src={imageUrl}
                alt={descriptionHtml?.slice(0, 150) || ''}
                loading="lazy"
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                style={{ objectFit: 'cover' }}
              />
            </Box>
          </Grid>
        )}

        {/* information */}
        <Grid
          item
          xs={12}
          md={imageUrl ? 6 : 12}
          sx={{
            order: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isDesktop ? 'center' : 'flex-start',
            alignItems: isDesktop ? 'flex-start' : 'center',
            width: '100%',
            bgcolor: '#F5F5F6',
            height: { xs: 'auto', md: 400 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              px: { xs: 2, md: 3 },
              py: { xs: 2, md: 4 },
            }}
          >
            {title && (
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#222' }}>
                {title}
              </Typography>
            )}

            {isDesktop ? (
              <StyledScroll
                sx={{
                  height: 300,
                  color: '#575758',
                  fontSize: '0.875rem',
                  pr: 1,
                }}
                dangerouslySetInnerHTML={{
                  __html: descriptionHtml.replace(/(?:\r\n|\r|\n)/g, '<br/>'),
                }}
              />
            ) : (
              <>
                <Box
                  ref={contentRef}
                  sx={{
                    position: 'relative',
                    maxHeight: expanded ? 'none' : 200,
                    overflow: 'hidden',
                    transition: 'max-height 0.3s',
                    color: '#575758',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-line',
                    wordBreak: 'break-word',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: descriptionHtml.replace(/(?:\r\n|\r|\n)/g, '<br/>'),
                  }}
                />
                {showReadMore && (
                  <Box textAlign="center" mt={1}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setExpanded((prev) => !prev)}
                      endIcon={
                        expanded ? (
                          <RemoveCircleOutlineIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                        ) : (
                          <ControlPointIcon sx={{ color: (theme) => theme.palette.primary.main }} />
                        )
                      }
                      sx={{
                        color: '#000',
                        textTransform: 'none',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      {expanded ? t('read-less') : t('read-all')}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
